import * as moment from 'moment';
import { getLocale } from 'services/LocalizationService';

export function formatEventTime(localizedTimeObj) {
    const nowMoment = moment();
    const localizedMoment = moment(localizedTimeObj);

    if (localizedMoment.format('YYYY MM DD') === nowMoment.format('YYYY MM DD')) {
        // Streaming today
        let optionsToday = {
            hour: 'numeric',
            minute: 'numeric',
        };
        return localizedTimeObj.toLocaleString(CBS.Registry.region.locale, optionsToday);
    }

    if (localizedMoment.diff(nowMoment, 'days', true) <= 6) {
        // Streaming this week
        let optionsWeek = {
            weekday: 'short',
            hour: 'numeric',
            minute: 'numeric',
        };
        return localizedTimeObj.toLocaleString(CBS.Registry.region.locale, optionsWeek);
    }

    // Streaming in the future
    let optionsFuture = {
        day: 'numeric',
        month: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    };
    return localizedTimeObj.toLocaleString(CBS.Registry.region.locale, optionsFuture);
}

export function formatSpotlightTime(streamStartDate, streamEndDate, startDate, endDate) {
    const nowMoment = moment().locale(CBS.Registry.region.locale);
    const streamStartMoment = moment(streamStartDate).locale(CBS.Registry.region.locale);
    const streamEndMoment = moment(streamEndDate).locale(CBS.Registry.region.locale);
    const startMoment = moment(startDate).locale(CBS.Registry.region.locale);
    const endMoment = moment(endDate).locale(CBS.Registry.region.locale);
    const diffTime = startDate - Date.now();
    const durationMoment = moment.duration(diffTime, 'milliseconds');
    const days = durationMoment.days();
    const hours = durationMoment.hours();
    const mins = durationMoment.minutes();

    let isUpcoming = false;
    let isLive = false;
    let isComplete = false;

    if (nowMoment < startMoment) {
        isUpcoming = true;
    }

    if (startMoment < nowMoment && nowMoment < streamEndMoment) {
        isLive = true;
        isUpcoming = false;
    }

    if (nowMoment > streamEndMoment) {
        isComplete = true;
        isLive = false;
        isUpcoming = false;
    }

    // 7 days or more from now
    // 4/3 5:00–8:30 PM
    if (isUpcoming && days >= 7) {
        return startMoment.format('M/D h:mm') + '-' + endMoment.format('h:mm a');
    }

    // Within 6 days (and over 24 hours from now)
    // SUN 5:00–8:30 PM
    if (isUpcoming && days <= 6 && days > 1) {
        return startMoment.format('ddd h:mm') + '-' + endMoment.format('h:mm a');
    }

    // Upcoming within 24 hours and Pre-show broadcasting
    // 5:00–8:30 PM
    if (isUpcoming && days < 1) {
        return startMoment.format('h:mm') + '-' + endMoment.format('h:mm a');
    }

    // Live Now
    // 2H 18M LEFT
    if (isLive) {
        let endDiffTime = streamEndMoment - nowMoment;
        let endDurationMoment = moment.duration(endDiffTime, 'milliseconds');
        let endYears = endDurationMoment.years();
        let endMonths = endDurationMoment.months();
        let endDays = endDurationMoment.days();
        let endHours = endDurationMoment.hours();
        let endMins = endDurationMoment.minutes();

        // add days/months/years (since we only care about hours)
        if (endDays > 0) {
            endHours = endHours + endDays * 24;
        }
        if (endMonths > 0) {
            endHours = endHours + endMonths * 30 * 24;
        }
        if (endYears > 0) {
            endHours = endHours + endYears * 365 * 24;
        }

        return (
            endHours +
            getLocale('hour_abbreviation') +
            ' ' +
            endMins +
            getLocale('minute_abbreviation') +
            ' ' +
            getLocale('left')
        );
    }

    // Event over (complete)
    // show nothing
    if (isComplete) {
        return '';
    }
}

/**
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * for date and time style options
 *
 * @returns Formatted date using the localized date
 */
export function formatDate(isoDate, dateStyle = 'medium', timeStyle) {
    let formatted = '';
    const options = {};

    if (dateStyle === 'year-only') {
        options.year = 'numeric';
    } else {
        if (dateStyle) options.dateStyle = dateStyle;
        if (timeStyle) options.timeStyle = timeStyle;
    }

    try {
        formatted = new Intl.DateTimeFormat(CBS.Registry.region.locale, options).format(
            new Date(isoDate),
        );
    } catch (e) {
        console.error('Error formatting date: ' + isoDate, e);
    }

    return formatted;
}

/**
 * Localizes the time elements in a given container, and displays
 * them given the date and time styles set in the data attributes
 */
export function formatTimeElements(container) {
    const element = container || document;
    const times = element?.getElementsByTagName('time');

    if (!times) return;

    Array.from(times).forEach((timeEl) => {
        try {
            const dateStyle = timeEl.dataset.dateStyle || 'medium';
            const dateTime = new Date(timeEl.dateTime);
            const timeStyle = timeEl.dataset.timeStyle;

            if (!dateStyle && !timeStyle) return;

            timeEl.innerText = formatDate(dateTime, dateStyle, timeStyle);
        } catch (e) {
            console.log(`Error formatting date: ${timeEl.dateTime}`);
        }
    });
}
